import { useMemo, useState } from 'react'

import { AdvancedMarker } from '@vis.gl/react-google-maps'

import type { TLote } from '@/features/empreendimentos/http'
import { corPorStatus } from '@/features/empreendimentos/utils/cores'
import { cn } from '@/infra'
import { formatToBRL } from '@/utils/currency.utils'

import { Polygon } from './polygon'

interface LoteMarkerProps {
  lote: TLote
  onClick: () => void
}

export const LoteMarker = ({ lote, onClick }: LoteMarkerProps) => {
  const corLote = corPorStatus(lote.tipoLegendaStatus, lote.cor)

  const [tooltipOpen, setTooltipOpen] = useState(false)

  const paths = useMemo(() => {
    if (!lote.coordenadas || lote.coordenadas.length === 0) {
      return []
    }
    return lote.coordenadas.map((coord) => ({
      lat: coord.latitude,
      lng: coord.longitude,
    }))
  }, [lote.coordenadas])

  const center = useMemo(() => {
    if (!lote.coordenadas || lote.coordenadas.length === 0) {
      return { lat: 0, lng: 0 }
    }
    let lat = 0
    let lng = 0

    for (const coordenada of lote.coordenadas) {
      lat += coordenada.latitude
      lng += coordenada.longitude
    }

    return {
      lat: lat / lote.coordenadas.length,
      lng: lng / lote.coordenadas.length,
    }
  }, [lote.coordenadas])

  if (paths.length === 0) {
    return null
  }

  return (
    <div>
      <Polygon
        className={cn('flex cursor-pointer items-center justify-center rounded-full font-medium')}
        paths={paths}
        fillColor={corLote.fill}
        strokeColor="black"
        fillOpacity={0.45}
        strokeOpacity={0.1}
        onClick={() => {
          setTooltipOpen(false)
          onClick()
        }}
        onMouseOver={() => {
          setTooltipOpen(true)
        }}
        onMouseOut={() => {
          setTooltipOpen(false)
        }}
      />

      {tooltipOpen && (
        <AdvancedMarker position={center} anchorPoint={['50%', '50%']}>
          <div className="bg-primary text-primary-foreground animate-in fade-in-0 zoom-in-95 z-50 max-w-sm translate-y-15 rounded-md px-3 py-1.5 text-xs font-medium shadow-md transition-transform">
            <p>{formatToBRL(lote.valor || 0)}</p>
          </div>
        </AdvancedMarker>
      )}
    </div>
  )
}
